import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Save, 
  ArrowLeft, 
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  GripVertical,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Restaurant', icon: '🍽️' },
  { value: 'grocery', label: 'Grocery Store', icon: '🛒' },
  { value: 'service', label: 'Service Provider', icon: '🔧' }
];

const DEFAULT_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Orange
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#0EA5E9', // Light Blue
  '#22C55E', // Success Green
  '#EF4444'  // Red
];

export default function AdminWorkflowBuilder() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isEditMode = !!templateId;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [businessType, setBusinessType] = useState('restaurant');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [stages, setStages] = useState([]);
  const [transitions, setTransitions] = useState([]);
  
  // Validation
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (isEditMode) {
      fetchTemplate();
    } else {
      // Initialize with basic structure
      setStages([
        { stage_id: 'placed', name: 'placed', display_name: 'Placed', order: 1, color: '#3B82F6', is_final: false, can_cancel_from: true },
        { stage_id: 'delivered', name: 'delivered', display_name: 'Delivered', order: 2, color: '#22C55E', is_final: true, can_cancel_from: false },
        { stage_id: 'cancelled', name: 'cancelled', display_name: 'Cancelled', order: 3, color: '#EF4444', is_final: true, can_cancel_from: false }
      ]);
      setTransitions([
        { from_stage: 'placed', to_stage: 'delivered', label: 'Mark as Delivered', requires_confirmation: false }
      ]);
    }
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const response = await api.get(`/admin/workflow-templates/${templateId}`);
      
      const template = response.data.template;
      setName(template.name);
      setBusinessType(template.business_type);
      setDescription(template.description || '');
      setIsDefault(template.is_default);
      setStages(template.stages);
      setTransitions(template.transitions);
    } catch (error) {
      console.error('Failed to fetch template:', error);
      toast.error('Failed to load workflow template');
      navigate('/admin/workflows');
    } finally {
      setLoading(false);
    }
  };

  const addStage = () => {
    const newOrder = Math.max(...stages.map(s => s.order), 0) + 1;
    const newStage = {
      stage_id: `stage_${Date.now()}`,
      name: `stage_${newOrder}`,
      display_name: `New Stage ${newOrder}`,
      order: newOrder,
      color: DEFAULT_COLORS[newOrder % DEFAULT_COLORS.length],
      is_final: false,
      can_cancel_from: true
    };
    setStages([...stages, newStage]);
  };

  const updateStage = (index, field, value) => {
    const updated = [...stages];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-update stage_id and name if display_name changes
    if (field === 'display_name') {
      const nameValue = value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      updated[index].stage_id = nameValue;
      updated[index].name = nameValue;
    }
    
    setStages(updated);
  };

  const removeStage = (index) => {
    const stageToRemove = stages[index];
    
    // Remove transitions referencing this stage
    const updatedTransitions = transitions.filter(
      t => t.from_stage !== stageToRemove.name && t.to_stage !== stageToRemove.name
    );
    
    setStages(stages.filter((_, i) => i !== index));
    setTransitions(updatedTransitions);
  };

  const moveStage = (index, direction) => {
    const updated = [...stages];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (swapIndex < 0 || swapIndex >= updated.length) return;
    
    const temp = updated[index];
    updated[index] = updated[swapIndex];
    updated[swapIndex] = temp;
    
    // Update order
    updated.forEach((stage, i) => {
      stage.order = i + 1;
    });
    
    setStages(updated);
  };

  const addTransition = () => {
    if (stages.length < 2) {
      toast.error('Add at least 2 stages before creating transitions');
      return;
    }
    
    const newTransition = {
      from_stage: stages[0].name,
      to_stage: stages[1].name,
      label: 'New Transition',
      requires_confirmation: false
    };
    setTransitions([...transitions, newTransition]);
  };

  const updateTransition = (index, field, value) => {
    const updated = [...transitions];
    updated[index] = { ...updated[index], [field]: value };
    setTransitions(updated);
  };

  const removeTransition = (index) => {
    setTransitions(transitions.filter((_, i) => i !== index));
  };

  const validateWorkflow = () => {
    const validationErrors = [];
    const validationWarnings = [];

    if (!name.trim()) validationErrors.push('Workflow name is required');
    if (stages.length === 0) validationErrors.push('At least one stage is required');
    
    const stageNames = stages.map(s => s.name);
    const uniqueNames = new Set(stageNames);
    if (stageNames.length !== uniqueNames.size) {
      validationErrors.push('Duplicate stage names detected');
    }

    // Check transitions reference valid stages
    transitions.forEach((t, i) => {
      if (!stageNames.includes(t.from_stage)) {
        validationErrors.push(`Transition ${i + 1}: invalid from_stage "${t.from_stage}"`);
      }
      if (!stageNames.includes(t.to_stage)) {
        validationErrors.push(`Transition ${i + 1}: invalid to_stage "${t.to_stage}"`);
      }
    });

    // Check for orphan stages
    const stagesInTransitions = new Set();
    transitions.forEach(t => {
      stagesInTransitions.add(t.from_stage);
      stagesInTransitions.add(t.to_stage);
    });
    
    stages.forEach(s => {
      if (!s.is_final && !stagesInTransitions.has(s.name)) {
        validationWarnings.push(`Stage "${s.display_name}" has no transitions`);
      }
    });

    setErrors(validationErrors);
    setWarnings(validationWarnings);
    return validationErrors.length === 0;
  };

  const handleSave = async () => {
    if (!validateWorkflow()) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name,
        business_type: businessType,
        description,
        is_default: isDefault,
        stages,
        transitions
      };

      if (isEditMode) {
        await api.put(`/admin/workflow-templates/${templateId}`, payload);
        toast.success('Workflow template updated successfully');
      } else {
        await api.post('/admin/workflow-templates', payload);
        toast.success('Workflow template created successfully');
      }

      navigate('/admin/workflows');
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to save workflow';
      toast.error(message);
      
      // Show validation errors from backend
      if (error.response?.data?.detail?.errors) {
        setErrors(error.response.data.detail.errors);
      }
      if (error.response?.data?.detail?.warnings) {
        setWarnings(error.response.data.detail.warnings);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="h-96 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" data-testid="workflow-builder">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/workflows')}
              data-testid="back-btn"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 
                className="text-3xl font-bold" 
                style={{ color: '#222146', fontFamily: 'DM Serif Display, serif' }}
              >
                {isEditMode ? 'Edit Workflow' : 'Create Workflow'}
              </h1>
              <p className="text-sm text-gray-600">
                {isEditMode ? 'Modify workflow template configuration' : 'Build a new workflow template from scratch'}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#9A031E' }}
            data-testid="save-workflow-btn"
          >
            <Save size={20} className="mr-2" />
            {saving ? 'Saving...' : 'Save Workflow'}
          </Button>
        </div>

        {/* Validation Messages */}
        {(errors.length > 0 || warnings.length > 0) && (
          <Card className="p-4 space-y-2">
            {errors.map((error, i) => (
              <div key={`error-${i}`} className="flex items-start gap-2 text-red-600">
                <AlertCircle size={20} className="mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            ))}
            {warnings.map((warning, i) => (
              <div key={`warning-${i}`} className="flex items-start gap-2 text-yellow-600">
                <AlertCircle size={20} className="mt-0.5" />
                <span className="text-sm">{warning}</span>
              </div>
            ))}
          </Card>
        )}

        {/* Basic Info */}
        <Card className="p-6 space-y-4">
          <h2 className="text-xl font-semibold" style={{ color: '#222146' }}>Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workflow Name *
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Restaurant Order Workflow"
                data-testid="workflow-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Type *
              </label>
              <select
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9A031E] focus:border-transparent"
                data-testid="business-type-select"
              >
                {BUSINESS_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this workflow..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9A031E] focus:border-transparent"
              data-testid="workflow-description-input"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is-default"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 focus:ring-[#9A031E]"
              data-testid="is-default-checkbox"
            />
            <label htmlFor="is-default" className="text-sm text-gray-700">
              Set as default workflow for this business type
            </label>
          </div>
        </Card>

        {/* Stages */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold" style={{ color: '#222146' }}>
              Workflow Stages ({stages.length})
            </h2>
            <Button
              onClick={addStage}
              variant="outline"
              size="sm"
              data-testid="add-stage-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Stage
            </Button>
          </div>

          <div className="space-y-3">
            {stages.map((stage, index) => (
              <div 
                key={stage.stage_id} 
                className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg"
                data-testid={`stage-${index}`}
              >
                {/* Drag Handle */}
                <div className="flex flex-col gap-1 pt-2">
                  <button
                    onClick={() => moveStage(index, 'up')}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <GripVertical size={16} />
                  </button>
                  <button
                    onClick={() => moveStage(index, 'down')}
                    disabled={index === stages.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <GripVertical size={16} />
                  </button>
                </div>

                {/* Stage Fields */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Display Name</label>
                    <Input
                      value={stage.display_name}
                      onChange={(e) => updateStage(index, 'display_name', e.target.value)}
                      placeholder="Stage display name"
                      size="sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
                    <input
                      type="color"
                      value={stage.color}
                      onChange={(e) => updateStage(index, 'color', e.target.value)}
                      className="w-full h-10 rounded border border-gray-300 cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center gap-4 pt-6">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={stage.is_final}
                        onChange={(e) => updateStage(index, 'is_final', e.target.checked)}
                        className="rounded"
                      />
                      Final
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={stage.can_cancel_from}
                        onChange={(e) => updateStage(index, 'can_cancel_from', e.target.checked)}
                        className="rounded"
                      />
                      Cancelable
                    </label>
                  </div>

                  <div className="flex items-center justify-end pt-6">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeStage(index)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Transitions */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold" style={{ color: '#222146' }}>
              Transitions ({transitions.length})
            </h2>
            <Button
              onClick={addTransition}
              variant="outline"
              size="sm"
              disabled={stages.length < 2}
              data-testid="add-transition-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Transition
            </Button>
          </div>

          <div className="space-y-3">
            {transitions.map((transition, index) => (
              <div 
                key={index} 
                className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg"
                data-testid={`transition-${index}`}
              >
                <ArrowRight size={20} className="mt-2 text-gray-400" />
                
                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">From Stage</label>
                    <select
                      value={transition.from_stage}
                      onChange={(e) => updateTransition(index, 'from_stage', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#9A031E]"
                    >
                      {stages.map(s => (
                        <option key={s.stage_id} value={s.name}>{s.display_name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">To Stage</label>
                    <select
                      value={transition.to_stage}
                      onChange={(e) => updateTransition(index, 'to_stage', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#9A031E]"
                    >
                      {stages.map(s => (
                        <option key={s.stage_id} value={s.name}>{s.display_name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Button Label</label>
                    <Input
                      value={transition.label}
                      onChange={(e) => updateTransition(index, 'label', e.target.value)}
                      placeholder="e.g., Confirm Order"
                      size="sm"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-6">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={transition.requires_confirmation}
                        onChange={(e) => updateTransition(index, 'requires_confirmation', e.target.checked)}
                        className="rounded"
                      />
                      Require Confirmation
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTransition(index)}
                      className="text-red-600 hover:bg-red-50 ml-auto"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Preview */}
        <Card className="p-6 space-y-4">
          <h2 className="text-xl font-semibold" style={{ color: '#222146' }}>Workflow Preview</h2>
          <div className="flex flex-wrap gap-2">
            {stages.map((stage, index) => (
              <React.Fragment key={stage.stage_id}>
                <Badge
                  style={{ 
                    backgroundColor: stage.color,
                    color: 'white',
                    padding: '8px 16px'
                  }}
                >
                  {stage.display_name}
                  {stage.is_final && ' (Final)'}
                </Badge>
                {index < stages.length - 1 && (
                  <ArrowRight size={20} className="text-gray-400 mt-1" />
                )}
              </React.Fragment>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
